import {useEffect, useRef, useSyncExternalStore} from "react";
import {authenticatedSessionUid} from "auth/authProviders";
import {AccountApi} from "./AccountApi";
import {applyAccountToUser} from "./subscriptionSync";
import {User} from "./User";
import {useUser} from "../UserProvider";
import {
	PLAN_SETTLE_READ_TIMEOUT_MS,
	endPlacedOrderWait,
	placedOrderSnapshot,
	subscribePlacedOrders,
	waitForPaidPlan,
} from "./placedOrderSettle";

/**
 * Re-reads the signed-in account after an in-page subscription order, until it shows the plan the order
 * bought (kitecorp/transferify-ui#3679).
 *
 * A thin adapter by mandate (epic #26): when the wait ends and what counts as the plan having arrived are
 * `./placedOrderSettle`'s. This only starts the wait when an order is announced and projects the answer onto
 * the user, through the one mapping every `/account` re-read goes through.
 *
 * <b>Mounted ONCE, on the app shell.</b> The surfaces that place an order close as soon as it is placed (the
 * upgrade dialog's `onPlaced` is its close), so a wait owned by any of them would be cancelled by its own
 * unmount, and a shell mount is the one that outlives every one of them.
 *
 * <b>The wait belongs to the account that ordered.</b> A sign-out or another sign-in ends it. Each read is
 * bound to that account's Firebase uid, which refuses another session's token before the request is sent, and
 * before every read and every applied answer the wait asks the Firebase session itself who is signed in, as
 * well as the drawn user. The session, not only the drawn user: Firebase changes its user before the app's
 * listener runs, and React draws the listener's sign-out later still, so an answer landing in between would
 * pass a check of the drawn user and write the account that left back over the sign-out.
 */
export function usePlacedOrderAccountRefresh(): void {
	const [user, setUser] = useUser();
	// Read through a ref so the effect below does not restart on every change to the user, starting with the
	// one this very wait causes. It restarts on a change of WHO is signed in, and its cleanup then ends the wait.
	const userRef = useRef(user);
	userRef.current = user;
	const signedInId = user.isLoggedIn ? user.id : null;
	const {generation, settling} = useSyncExternalStore(subscribePlacedOrders, placedOrderSnapshot, placedOrderSnapshot);

	useEffect(() => {
		// The live snapshot as well as this render's: when the account changes mid-wait, the cleanup below has
		// just ended the wait that this render still reads as settling, and none may start for the newcomer.
		if (!settling || signedInId === null || !placedOrderSnapshot().settling) {
			return;
		}
		const startedFor = signedInId;
		const cancel = waitForPaidPlan({
			readAccount: () => AccountApi.getAccount({expectedFirebaseUid: startedFor, timeoutMs: PLAN_SETTLE_READ_TIMEOUT_MS}),
			stillSignedIn: () => authenticatedSessionUid() === startedFor
				&& userRef.current.isLoggedIn && userRef.current.id === startedFor,
			onPaidPlan: account => {
				const next = new User(userRef.current);
				applyAccountToUser(next, account);
				setUser(next);
			},
			onEnd: () => endPlacedOrderWait(generation),
		});
		return () => {
			cancel();
			// A wait nobody runs is over; ending it lets the surfaces that show "confirming" stop showing it.
			endPlacedOrderWait(generation);
		};
	}, [generation, settling, signedInId, setUser]);
}
